import React from 'react';
import { Draggable, useExerciseDnd, type DragOptionData } from '../../../dnd';

export interface SingleDragOptionProps {
  /** Unique DnD id for this chip (e.g. "drag-option-0" or "_inline_drag_opt_0") */
  optionId: string;
  /** Display value of the chip */
  value: string;
  /** Called when the chip is clicked instead of dragged */
  onOptionClick?: (value: string) => void;
  /** Extra class applied to the outer slot wrapper */
  className?: string;
}

/**
 * SingleDragOption - one draggable chip with its empty-slot counterpart.
 *
 * Shared between:
 *  - DragOptions (the panel widget — renders many chips in a pill background)
 *  - InlineDragOption (the inline widget — renders one chip inline in text flow)
 *
 * When the chip is currently "in use" (dropped into an input), an empty slot
 * is rendered in its place so the layout stays stable.
 */
export const SingleDragOption: React.FC<SingleDragOptionProps> = ({
  optionId,
  value,
  onOptionClick,
  className = '',
}) => {
  const { usedOptionIds } = useExerciseDnd();
  const isUsed = usedOptionIds.has(optionId);

  return (
    <div className={`drag-option-slot ${className}`.trim()}>
      {isUsed ? (
        <div className="drag-option-empty" aria-hidden="true" />
      ) : (
        <Draggable
          id={optionId}
          type="drag-option"
          data={{ value, label: value } as DragOptionData}
          className="drag-option-wrapper"
        >
          <button
            className="drag-option"
            onClick={() => onOptionClick?.(value)}
          >
            {value}
          </button>
        </Draggable>
      )}
    </div>
  );
};
